'use client';

import { usePathname, useRouter } from 'next/navigation';
import type { PromptBarMode } from './usePromptBarMode';
import { useCurrentMission } from './useCurrentMission';

type MissionContext = 'candidate' | 'company';

export interface ActionItem {
  key: string;
  /** Which context this mission belongs to — used only to group the items in the menu. */
  context: MissionContext;
  /** Destination URL, used when no inAppAction is provided. */
  href: string;
  /** Optional soft-navigation fast path (no full reload). */
  inAppAction?: () => void;
  selected: boolean;
}

interface PromptBarActions {
  candidateItems: ActionItem[];
  companyItems: ActionItem[];
  handleClick: (item: ActionItem) => void;
}

const loginRedirect = (target: string) => `/login?_target_path=${encodeURIComponent(target)}`;

/**
 * Builds the candidate/company mission items and the click handler shared by every
 * prompt-bar navigation surface (the `+` popup and the mission chips). Items carry no
 * label — every surface labels them itself, via useMissionNavItems.
 */
export const usePromptBarActions = (
  locale: Locale,
  mode: PromptBarMode,
  isAuthenticated: boolean,
): PromptBarActions => {
  const pathname = usePathname();
  const router = useRouter();
  const mission = useCurrentMission(locale);

  // The "Jobs" chip points at the canonical job-list home (/locuri-de-munca · /jobs) — no /mc.
  // Spelled out per-locale because getI18nLinkHref can't resolve the param-less /job/list base.
  const jobsHomePath = locale === 'en' ? '/en/jobs' : '/locuri-de-munca';
  const interviewsPath = `/${locale}/stories`;
  const bestieChatPath = `/${locale}/bestie-chat`;
  // The "My Jobs" chip always targets `/my-jobs`; the server branches by wallet there (companies
  // stay; candidates are redirected to /applied-jobs). This keeps the post-login redirect correct
  // for both roles without needing the (client-only) wallet flag here.
  const myJobsPath = `/${locale}/my-jobs`;
  const addJobPath = `/${locale}/job-add/edit`;
  const editProfilePath = `/${locale}/profile`;

  const isSelected = (key: string): boolean => mission.key === key;

  const candidateItems: ActionItem[] = [
    {
      key: 'interviews',
      context: 'candidate',
      // Stories/interviews live on the standalone /stories page now (split out of coaching chat).
      href: interviewsPath,
      selected: isSelected('interviews'),
    },
    {
      key: 'jobs',
      context: 'candidate',
      href: jobsHomePath,
      selected: isSelected('jobs'),
    },
    {
      key: 'edit-profile',
      context: 'candidate',
      href: isAuthenticated ? editProfilePath : loginRedirect(editProfilePath),
      selected: isSelected('edit-profile'),
    },
    {
      key: 'coaching',
      context: 'candidate',
      href: bestieChatPath,
      selected: isSelected('coaching'),
    },
  ];

  const cvSearchPath = `/${locale}/cv/search`;

  const companyItems: ActionItem[] = [
    {
      key: 'talents',
      context: 'company',
      // No login redirect: /cv/search is guest-accessible (locked previews; the page only
      // bounces to login if the talent-search API itself demands it).
      href: cvSearchPath,
      // Hard navigation (full reload): a soft router.push to /cv/search gets caught by the
      // (.)cv/[slug] intercepting route — "search" matches [slug] and opens the CV-detail
      // modal (404s on /v2/user-resume-detail/search). Same workaround as
      // TalentSearchPrompt.tsx and SearchSection.tsx.
      inAppAction: () => {
        window.location.href = cvSearchPath;
      },
      selected: isSelected('talents'),
    },
    {
      key: 'add-job',
      context: 'company',
      href: isAuthenticated ? addJobPath : loginRedirect(addJobPath),
      selected: isSelected('add-job'),
    },
    {
      key: 'candidate-management',
      context: 'company',
      href: isAuthenticated ? myJobsPath : loginRedirect(myJobsPath),
      selected: isSelected('candidate-management'),
    },
  ];

  const isOnSamePage = (href: string): boolean => {
    if (!pathname) return false;
    const localePrefix = `/${locale}`;
    const stripLocale = (p: string): string => {
      if (p === localePrefix) return '/';
      if (p.startsWith(`${localePrefix}/`)) return p.slice(localePrefix.length);
      return p;
    };
    const normalize = (p: string): string => {
      const stripped = stripLocale(p.split('?')[0]);
      return stripped === '/' ? stripped : stripped.replace(/\/$/, '');
    };
    return normalize(href) === normalize(pathname);
  };

  const handleClick = (item: ActionItem) => {
    if (item.selected) return;
    if (typeof window === 'undefined') return;

    // Mission selection never switches context — only the explicit sidebar switch does. A
    // cross-context mission just navigates; its page renders inside whichever context shell
    // is currently mounted (pages are shell-agnostic). Nothing to do when already there.
    if (isOnSamePage(item.href)) return;

    if (item.inAppAction) {
      item.inAppAction();
      return;
    }
    // Soft-navigate within the App Router (keeps the persistent MC shell mounted). For
    // Pages-Router targets (e.g. the /login redirect) Next falls back to a hard navigation.
    router.push(item.href);
  };

  return { candidateItems, companyItems, handleClick };
};
